import { PEOPLE_SCHEMAS, SHEET_BY_ID, type PeopleData, type PeopleIssue, type PeopleSheet } from '@lg/shared';

/** A stored people import row before typing (spec 6 PeopleImportRow). */
export interface RawPeopleRow {
  id: string;
  sheet: PeopleSheet;
  rowNumber: number;
  data: Record<string, unknown>;
}

export interface ParseResult {
  data: PeopleData;
  issues: PeopleIssue[];
}

export function emptyPeopleData(): PeopleData {
  return {
    users: [],
    workers: [],
    positions: [],
    assignments: [],
    approvalLimits: [],
    roles: [],
    customers: [],
    vendors: [],
    projectContracts: [],
    projects: [],
  };
}

/**
 * Rule P1: parse and normalise every row, then flag duplicate keys within a
 * sheet. Only rows that parse cleanly reach the cross-row rules.
 */
export function parsePeopleRows(rows: readonly RawPeopleRow[]): ParseResult {
  const data = emptyPeopleData();
  const issues: PeopleIssue[] = [];
  const seenKeys = new Map<string, number>();

  const sorted = [...rows].sort((a, b) => a.sheet.localeCompare(b.sheet) || a.rowNumber - b.rowNumber);
  for (const row of sorted) {
    const parsed = PEOPLE_SCHEMAS[row.sheet].safeParse(row.data);
    if (!parsed.success) {
      const seenColumns = new Set<string>();
      for (const issue of parsed.error.issues) {
        const column = issue.path.join('.');
        if (seenColumns.has(column)) continue; // one message per cell
        seenColumns.add(column);
        issues.push({
          rule: 'P1',
          sheet: row.sheet,
          rowNumber: row.rowNumber,
          column: issue.path.join('.') || null,
          severity: 'error',
          message: issue.message,
        });
      }
      continue;
    }
    const value = parsed.data as Record<string, unknown>;
    const def = SHEET_BY_ID[row.sheet];
    const key = `${row.sheet}|${def.key.map((k) => String(value[k])).join('|')}`;
    const firstRow = seenKeys.get(key);
    if (firstRow !== undefined) {
      issues.push({
        rule: 'P1',
        sheet: row.sheet,
        rowNumber: row.rowNumber,
        column: def.key.join('+'),
        severity: 'error',
        message: `Duplicate of row ${firstRow} (${def.key.map((k) => String(value[k])).join(', ')})`,
      });
      continue;
    }
    seenKeys.set(key, row.rowNumber);
    (data[row.sheet] as Array<Record<string, unknown>>).push({ ...value, rowNumber: row.rowNumber });
  }
  return { data, issues };
}

/** Normalise a row's values for storage (trim, case) without failing on bad input. */
export function normaliseRowData(sheet: PeopleSheet, data: Record<string, unknown>): Record<string, unknown> {
  const parsed = PEOPLE_SCHEMAS[sheet].safeParse(data);
  if (parsed.success) return parsed.data as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const col of SHEET_BY_ID[sheet].columns) {
    const v = data[col.key];
    out[col.key] = typeof v === 'string' ? v.trim() : v;
  }
  return out;
}
